import type { AttachmentGetPutSignedUrlResponseDto } from '@govinity/validation';
import type {
  PublicCommentCreateBody,
  PublicCommentCreateResponse,
  PublicCommentOptionsResponse,
  PublicCommentUserComment,
  PublicCommentUserCommentsResponse,
} from '@govinity/validation/public';

import { ReadOnlyApi } from '../base';

/**
 * Client for the pre-meeting public comment flow on a single meeting: the agenda entries open to
 * comments, the signed-in citizen's own comments, and the attachment upload that precedes a
 * comment carrying a document. Every route 403s when the government has public comments switched
 * off, which the portal surfaces by hiding the entry point.
 */
export class PublicCommentsApi extends ReadOnlyApi<
  PublicCommentUserComment,
  PublicCommentOptionsResponse
> {
  parentPath: string;
  governmentCode: string;
  meetingId: number;

  constructor(governmentCode: string, meetingId: number, parentPath: string) {
    super();

    this.parentPath = parentPath;
    this.governmentCode = governmentCode;
    this.meetingId = meetingId;
  }

  getPath() {
    return `${this.parentPath}/${this.governmentCode}/meetings/${this.meetingId}/public-comments`;
  }

  async getOptions() {
    const { data } = await this.request<PublicCommentOptionsResponse>({
      method: 'GET',
      url: this.getPath(),
    });

    return data;
  }

  async getUserComments() {
    const { data } = await this.request<PublicCommentUserCommentsResponse>({
      method: 'GET',
      url: `${this.getPath()}/user`,
    });

    return data;
  }

  async createUserComment(body: PublicCommentCreateBody) {
    const { data } = await this.request<PublicCommentCreateResponse>({
      method: 'POST',
      url: `${this.getPath()}/user`,
      data: body,
    });

    return data;
  }

  async deleteUserComment(commentId: number) {
    await this.request({
      method: 'DELETE',
      url: `${this.getPath()}/user/${commentId}`,
    });
  }

  async getPutSignedUrl(filename: string) {
    const { data } = await this.request<AttachmentGetPutSignedUrlResponseDto>({
      method: 'POST',
      url: `${this.getPath()}/put-signed-url`,
      data: { filename },
    });

    return data;
  }
}
